/**
 * CreateAISystemModal - AI System creation form.
 *
 * Flat single-step form: name, use case, description (pre-fills from use case),
 * owner. Look and feel mirrors ExperimentalOnboardingModal.
 */
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { Collapse } from '@material-ui/core';
import {
  DevHubAlert,
  DevHubAutocomplete,
  DevHubBox,
  DevHubButton,
  DevHubCheckbox,
  DevHubFormControlLabel,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubGrid,
  DevHubIconButton,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { PartySearch } from '../common/PartySearch';
import { ResponsibleParty } from '../insights/types/RoleTypes';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { CreateAISystemMutation } from './queries';
import {
  AiUseCase,
  CreateAISystemFormData,
  CreateAISystemResult,
} from './types';
import { useUseCaseApplicationMap } from './useUseCaseApplicationMap';
import { isActionableUseCase } from './useApprovedUseCases';
import {
  nameValidation,
  getNameErrorMessage,
  descriptionValidation,
  getDescriptionErrorMessage,
} from '../onboarding/experimentation/onboarding-modal/validation';
import { checkApplicationNameUniqueness } from '../../util/applicationValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * When set, the use case field is pre-selected and locked (read-only).
   * Used when launching from the Connections AI tab where the use case is already known.
   */
  lockedUseCaseId?: string;
  /**
   * Called after successful creation with the new application result. When provided,
   * the modal does NOT navigate away — the caller decides what happens next.
   * Defaults to navigating to the new application's detail page.
   */
  onSuccess?: (result: CreateAISystemResult['application']) => void;
}

const EMPTY_FORM: CreateAISystemFormData = {
  name: '',
  description: '',
  useCaseId: '',
};

const CurrentUserQuery = gql`
  query CreateAISystemModalCurrentUser($login: String!) {
    identity {
      user(login: $login) {
        login
        firstName
        lastName
        email
      }
    }
  }
`;

/**
 * Returns a validation error message, or null if the form is valid.
 * @param form - The current form data
 * @param ownerLogin - The resolved owner login (from identity API)
 */
function validate(
  form: CreateAISystemFormData,
  ownerLogin: string | undefined,
): string | null {
  if (!form.name.trim()) return 'AI System name is required';
  if (nameValidation(form.name)) return getNameErrorMessage(form.name);
  if (!form.useCaseId) return 'Select an approved use case';
  if (form.description && descriptionValidation(form.description))
    return getDescriptionErrorMessage(form.description);
  if (!ownerLogin) return 'Owner is required';
  return null;
}

/**
 * Modal for creating a new AI System, optionally pre-loaded with a use case.
 * @param root0 - Component props
 */
export const CreateAISystemModal = ({
  open,
  onClose,
  lockedUseCaseId,
  onSuccess,
}: Props) => {
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const { rows: useCaseRows, loading: useCasesLoading } =
    useUseCaseApplicationMap();
  const [form, setForm] = useState<CreateAISystemFormData>(EMPTY_FORM);
  const [selectedUseCase, setSelectedUseCase] = useState<AiUseCase | null>(
    null,
  );
  const [owner, setOwner] = useState<ResponsibleParty>({} as ResponsibleParty);
  const [nameTouched, setNameTouched] = useState(false);
  const [nameUniquenessError, setNameUniquenessError] = useState<
    string | undefined
  >(undefined);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [ownerTouched, setOwnerTouched] = useState(false);
  const [initialOwnerLoaded, setInitialOwnerLoaded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optionalExpanded, setOptionalExpanded] = useState(false);

  const [createAISystem, { error: mutationError }] = useMutation<{
    resources: { application: { createAISystem: CreateAISystemResult } };
  }>(CreateAISystemMutation);

  const [fetchCurrentUser] = useLazyQuery<{
    identity: { user: ResponsibleParty };
  }>(CurrentUserQuery);

  useEffect(() => {
    if (initialOwnerLoaded && !owner?.login) {
      setOwnerTouched(true);
    }
  }, [owner, initialOwnerLoaded]);

  useEffect(() => {
    if (!open) return;
    identityApi.getBackstageIdentity().then(async identity => {
      const login = identity.userEntityRef.split('/').pop() ?? '';
      if (!login) return;
      const result = await fetchCurrentUser({ variables: { login } });
      const user = result.data?.identity?.user;
      if (user) {
        setOwner(user);
        setInitialOwnerLoaded(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setSelectedUseCase(null);
    setOwner({} as ResponsibleParty);
    setNameTouched(false);
    setNameUniquenessError(undefined);
    setIsCheckingName(false);
    setDescriptionTouched(false);
    setOwnerTouched(false);
    setInitialOwnerLoaded(false);
    setOptionalExpanded(false);
    setValidationError(null);
    setIsSubmitting(false);
    onClose();
  };

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'name') setNameUniquenessError(undefined);
  };

  const handleSetOwner: Dispatch<SetStateAction<ResponsibleParty>> =
    useCallback(
      value => {
        const resolved = typeof value === 'function' ? value(owner) : value;
        setOwner(resolved);
        if (resolved?.login) setOwnerTouched(false);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [owner],
    );

  const handleNameBlur = async () => {
    setNameTouched(true);
    const name = form.name;
    if (!name || nameValidation(name)) {
      setNameUniquenessError(undefined);
      return;
    }
    setIsCheckingName(true);
    setNameUniquenessError(undefined);
    try {
      const result = await checkApplicationNameUniqueness(name);
      setNameUniquenessError(result.isUnique ? undefined : result.error);
    } catch {
      // fail open
    } finally {
      setIsCheckingName(false);
    }
  };

  const handleUseCaseChange = (_: unknown, value: AiUseCase | null) => {
    setSelectedUseCase(value);
    setDescriptionTouched(false);
    setForm(prev => ({
      ...prev,
      useCaseId: value?.useCaseId ?? '',
      description: value?.businessUseCase ?? prev.description,
    }));
  };

  // When a lockedUseCaseId is provided, pre-select that use case once the list loads.
  useEffect(() => {
    if (!lockedUseCaseId || !useCaseRows.length) return;
    const match =
      useCaseRows.find(r => r.useCase.useCaseId === lockedUseCaseId)?.useCase ??
      null;
    if (match && selectedUseCase?.useCaseId !== lockedUseCaseId) {
      handleUseCaseChange(null, match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedUseCaseId, useCaseRows]);

  const handleSubmit = async () => {
    const err = validate(form, owner?.login);
    if (err || nameUniquenessError || isCheckingName) {
      setValidationError(
        err ?? nameUniquenessError ?? 'Name check in progress, please wait.',
      );
      return;
    }
    setValidationError(null);
    setIsSubmitting(true);
    try {
      const { data } = await createAISystem({
        variables: {
          input: {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            useCaseId: form.useCaseId,
            ownerLogin: owner?.login || undefined,
            processesPI: form.processesPI ?? undefined,
            processesSPI: form.processesSPI ?? undefined,
            processesChildData: form.processesChildData ?? undefined,
            processesPartnerData: form.processesPartnerData ?? undefined,
            monitoringEnabled: form.monitoringEnabled ?? undefined,
          },
        },
      });

      const result = data?.resources?.application?.createAISystem;
      if (result?.success && result?.application?.applicationId) {
        handleClose();
        if (onSuccess) {
          onSuccess(result.application);
        } else {
          navigate(
            `/catalog/default/system/${result.application.applicationId}`,
          );
        }
      } else {
        setValidationError(
          result?.message ?? 'Creation failed. Please try again.',
        );
      }
    } catch {
      // error surfaced via mutationError
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasActionableUnlinkedUseCases = useCaseRows.some(
    r => !r.linkedApplication && isActionableUseCase(r.useCase),
  );
  const hasAnyApprovedUseCases = useCaseRows.some(r =>
    isActionableUseCase(r.useCase),
  );
  const allLinked = hasAnyApprovedUseCases && !hasActionableUnlinkedUseCases;

  return (
    <DevHubDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{ style: { width: '600px', maxWidth: '600px' } }}
    >
      <DevHubDialogTitle>
        <DevHubGrid
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <DevHubTypography variant="h5" weight="light" fontColor="purpleBlue">
            Create AI System
          </DevHubTypography>
          <DevHubIconButton onClick={handleClose}>
            <CloseIcon />
          </DevHubIconButton>
        </DevHubGrid>
      </DevHubDialogTitle>

      {!useCasesLoading &&
      !lockedUseCaseId &&
      !hasActionableUnlinkedUseCases ? (
        <>
          <DevHubDialogContent style={{ minHeight: '160px' }}>
            <DevHubBox
              padding="medium"
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: '120px' }}
            >
              <DevHubBox style={{ textAlign: 'center', maxWidth: '400px' }}>
                <DevHubTypography variant="h6" style={{ marginBottom: '8px' }}>
                  {allLinked
                    ? 'All use cases already linked'
                    : 'No approved use cases available'}
                </DevHubTypography>
                <DevHubTypography variant="body2" opacity="hazy">
                  {allLinked
                    ? 'Each of your approved use cases is already linked to an application. An AI System requires an unlinked approved use case.'
                    : 'You have no approved use cases available to create an AI System. Use cases must be approved in the AI governance process and not already linked to another application.'}
                </DevHubTypography>
              </DevHubBox>
            </DevHubBox>
          </DevHubDialogContent>
          <DevHubDialogActions
            style={{ padding: '16px 24px', justifyContent: 'flex-end' }}
          >
            <DevHubButton variant="outlined" onClick={handleClose}>
              Close
            </DevHubButton>
          </DevHubDialogActions>
        </>
      ) : (
        <>
          <DevHubDialogContent style={{ minHeight: '220px' }}>
            <DevHubBox padding="medium">
              <DevHubGrid container>
                <DevHubGrid item xs={12}>
                  <DevHubBox>
                    <DevHubTypography variant="body1">
                      {lockedUseCaseId
                        ? 'Name the AI System for this use case. Other fields can be updated as needed.'
                        : 'Give the AI System a name and select one of your approved AI use cases. Other fields can be updated as needed.'}
                    </DevHubTypography>
                  </DevHubBox>
                </DevHubGrid>

                <DevHubGrid item xs={12}>
                  <DevHubBox>
                    <DevHubTextField
                      name="name"
                      label="AI System Name *"
                      value={form.name}
                      onChange={handleFieldChange}
                      onBlur={handleNameBlur}
                      error={
                        nameTouched &&
                        (nameValidation(form.name) || !!nameUniquenessError)
                      }
                      helperText={(() => {
                        if (!nameTouched) return '';
                        if (nameValidation(form.name))
                          return getNameErrorMessage(form.name);
                        return (
                          nameUniquenessError ??
                          (isCheckingName
                            ? 'Checking name availability...'
                            : '')
                        );
                      })()}
                      variant="outlined"
                      size="medium"
                      fullWidth
                      noMargin
                    />
                  </DevHubBox>
                </DevHubGrid>

                <DevHubGrid item xs={12}>
                  <DevHubBox>
                    {lockedUseCaseId ? (
                      <DevHubTextField
                        label="Approved Use Case"
                        value={
                          selectedUseCase
                            ? `${selectedUseCase.useCaseId} — ${selectedUseCase.title}`
                            : lockedUseCaseId
                        }
                        variant="outlined"
                        size="medium"
                        fullWidth
                        noMargin
                        disabled
                      />
                    ) : (
                      <DevHubAutocomplete
                        options={[...useCaseRows]
                          .filter(r => isActionableUseCase(r.useCase))
                          .sort((a, b) => {
                            const aLinked = !!a.linkedApplication;
                            const bLinked = !!b.linkedApplication;
                            if (aLinked !== bLinked) return aLinked ? 1 : -1;
                            return a.useCase.useCaseId.localeCompare(
                              b.useCase.useCaseId,
                            );
                          })
                          .map(r => r.useCase)}
                        loading={useCasesLoading}
                        getOptionLabel={(uc: AiUseCase) =>
                          `${uc.useCaseId} — ${uc.title}`
                        }
                        getOptionDisabled={(uc: AiUseCase) =>
                          !!useCaseRows.find(
                            r => r.useCase.useCaseId === uc.useCaseId,
                          )?.linkedApplication
                        }
                        renderOption={(uc: AiUseCase) => {
                          const linked = useCaseRows.find(
                            r => r.useCase.useCaseId === uc.useCaseId,
                          )?.linkedApplication;
                          return (
                            <DevHubBox>
                              <DevHubTypography variant="body1">
                                {uc.useCaseId} — {uc.title}
                              </DevHubTypography>
                              {linked && (
                                <DevHubTypography
                                  variant="body2"
                                  opacity="hazy"
                                >
                                  Already linked to: {linked.name}
                                </DevHubTypography>
                              )}
                              {!linked && uc.businessUseCase && (
                                <DevHubTypography
                                  variant="body2"
                                  opacity="hazy"
                                >
                                  {uc.businessUseCase}
                                </DevHubTypography>
                              )}
                            </DevHubBox>
                          );
                        }}
                        value={selectedUseCase}
                        onChange={handleUseCaseChange}
                        renderInput={(params: object) => (
                          <DevHubTextField
                            {...params}
                            label="Approved Use Case *"
                            placeholder="Select an approved use case..."
                            variant="outlined"
                            size="medium"
                            fullWidth
                            noMargin
                          />
                        )}
                      />
                    )}
                  </DevHubBox>
                </DevHubGrid>

                <DevHubGrid item xs={12}>
                  <DevHubBox>
                    <DevHubTextField
                      name="description"
                      label="Description"
                      value={form.description}
                      onChange={handleFieldChange}
                      onBlur={() => {
                        if (form.description.trim().length > 0)
                          setDescriptionTouched(true);
                      }}
                      error={
                        descriptionTouched &&
                        descriptionValidation(form.description)
                      }
                      variant="outlined"
                      size="medium"
                      fullWidth
                      multiline
                      rows={3}
                      noMargin
                      helperText={
                        descriptionTouched &&
                        descriptionValidation(form.description)
                          ? getDescriptionErrorMessage(form.description)
                          : "Defaults to the use case's business description. Edit as needed."
                      }
                    />
                  </DevHubBox>
                </DevHubGrid>

                <DevHubGrid item xs={12}>
                  <PartySearch
                    key={initialOwnerLoaded ? 'owner-loaded' : 'owner-loading'}
                    name="owner"
                    label="Owner"
                    groupEnabled={false}
                    contractors
                    noMargin
                    required
                    error={ownerTouched && !owner?.login}
                    helperText={
                      ownerTouched && !owner?.login
                        ? 'Owner is required'
                        : 'Start typing to assign a different owner.'
                    }
                    onBlur={() => setOwnerTouched(true)}
                    setSelectedParty={handleSetOwner}
                    selectedParty={owner}
                  />
                </DevHubGrid>

                <DevHubGrid item xs={12}>
                  <DevHubBox style={{ marginTop: '8px' }}>
                    <DevHubButton
                      variant="text"
                      size="small"
                      onClick={() => setOptionalExpanded(prev => !prev)}
                      endIcon={
                        optionalExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      }
                    >
                      Optional attributes
                    </DevHubButton>
                    <Collapse in={optionalExpanded}>
                      <DevHubBox style={{ paddingTop: '8px' }}>
                        <DevHubTypography variant="body2" opacity="hazy">
                          These can be updated after creation.
                        </DevHubTypography>
                        {(
                          [
                            {
                              name: 'processesPI',
                              label: 'Processes Personal Information (PI)',
                            },
                            {
                              name: 'processesSPI',
                              label:
                                'Processes Sensitive Personal Information (SPI)',
                            },
                            {
                              name: 'processesChildData',
                              label: "Processes children's data",
                            },
                            {
                              name: 'processesPartnerData',
                              label: 'Processes partner data',
                            },
                            {
                              name: 'monitoringEnabled',
                              label: 'Monitoring enabled',
                            },
                          ] as {
                            name: keyof CreateAISystemFormData;
                            label: string;
                          }[]
                        ).map(({ name, label }) => (
                          <DevHubFormControlLabel
                            key={name}
                            control={
                              <DevHubCheckbox
                                checked={!!form[name]}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                  setForm(prev => ({
                                    ...prev,
                                    [name]: e.target.checked,
                                  }))
                                }
                                color="primary"
                              />
                            }
                            label={label}
                          />
                        ))}
                      </DevHubBox>
                    </Collapse>
                  </DevHubBox>
                </DevHubGrid>

                {(validationError || mutationError) && (
                  <DevHubGrid item xs={12}>
                    <DevHubBox>
                      <DevHubAlert severity="error">
                        {validationError ?? mutationError?.message}
                      </DevHubAlert>
                    </DevHubBox>
                  </DevHubGrid>
                )}
              </DevHubGrid>
            </DevHubBox>
          </DevHubDialogContent>

          <DevHubDialogActions
            style={{ padding: '16px 24px', justifyContent: 'space-between' }}
          >
            <DevHubBox>
              <DevHubButton
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </DevHubButton>
            </DevHubBox>
            <DevHubBox>
              <DevHubButton
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? <DevHubCircularProgress size={20} /> : 'Create'}
              </DevHubButton>
            </DevHubBox>
          </DevHubDialogActions>
        </>
      )}
    </DevHubDialog>
  );
};
